import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '../config.service';
import { AtlKeys, StaffAvailabilityType, StaffHistoryTrip, StaffTripAvailability, StandardTripWeek, TripStaffingDetail, TripStaffingFields, TripStaffingView } from './types/staffTripAvailability';
import { DataTransform, DateField } from './dataTransformFunctions';
import { Observable, forkJoin, of, switchMap } from 'rxjs';
import { DateTime } from 'luxon';
import { SubtripService } from './subtrip.service';
import { MinimalStaff, Trip, TripsSub } from './commonInterfaces';
import { TripService } from './trip.service';
import { StaffingTrip } from '../routed-modules/trip-staffing/tripStaffingTypes';

export interface GetLimitedStaffResponse {
  staff: MinimalStaff[]
  tlIds: number[]
  atlIds: number[]
}

interface PutStaffAvailabilityBody {
  startDate: string,
  endDate: string,
  type: StaffAvailabilityType | "clear",
  reloadYear?: number
}

interface StaffTripHistoryGetResult {
  tlTrips: StaffHistoryTrip[],
  atlTrips: StaffHistoryTrip[]
}

type StaffIdFields = Pick<TripStaffingView, "tlId" | "atl1" | "atl2" | "atl3" | "atl4" | "atl5" | "atl6" | "atl7" | "atl8" | "atl9" | "atl10" | "atl11" | "atl12" | "atl13" | "atl14" | "atl15" | "atl16">
type StaffConfirmedFields = Pick<TripStaffingView, "tlConfirmed" | "atl1Confirmed" | "atl2Confirmed" | "atl3Confirmed" | "atl4Confirmed" | "atl5Confirmed" | "atl6Confirmed" | "atl7Confirmed" | "atl8Confirmed" | "atl9Confirmed" | "atl10Confirmed" | "atl11Confirmed" | "atl12Confirmed" | "atl13Confirmed" | "atl14Confirmed" | "atl15Confirmed" | "atl16Confirmed">
@Injectable({
  providedIn: 'root'
})
export class TripStaffingService {

  private staffTripAvailabilityDateFields: DateField<StaffTripAvailability>[] = [
    { dateType: "local", fieldName: "startDate" },
    { dateType: "local", fieldName: "endDate" }
  ]
  private tripStaffingViewDateFields: DateField<TripStaffingView>[] = [
    { dateType: "local", fieldName: "tripDate" },
    { dateType: "local", fieldName: "endDate" },
    { dateType: "local", fieldName: "startingWeek" },
    { dateType: "local", fieldName: "endingWeek" },
    { dateType: "utc", fieldName: "staffingCompleteDate" },
    { dateType: "utc", fieldName: "noteEditedDate" }

  ]

  private tripWeeksDateFields: DateField<StandardTripWeek>[] = [
    { dateType: "local", fieldName: "startDate" },
    { dateType: "local", fieldName: "nextWeekStartDate" }
  ]

  private staffHistoryTripDateFields: DateField<StaffHistoryTrip>[] = [
    { dateType: "local", fieldName: "tripDate" }
  ]

  constructor(
    private http: HttpClient,
    private config: ConfigService,
    private subtripService: SubtripService,
    private tripService: TripService
  ) { }


  get minimumStaffPerTrip() {
    return 2
  }

  readonly staffConfirmedMap: { [property in keyof StaffIdFields]: keyof StaffConfirmedFields } = {
    "tlId": "tlConfirmed",
    "atl1": "atl1Confirmed",
    "atl2": "atl2Confirmed",
    "atl3": "atl3Confirmed",
    "atl4": "atl4Confirmed",
    "atl5": "atl5Confirmed",
    "atl6": "atl6Confirmed",
    "atl7": "atl7Confirmed",
    "atl8": "atl8Confirmed",
    "atl9": "atl9Confirmed",
    "atl10": "atl10Confirmed",
    "atl11": "atl11Confirmed",
    "atl12": "atl12Confirmed",
    "atl13": "atl13Confirmed",
    "atl14": "atl14Confirmed",
    "atl15": "atl15Confirmed",
    "atl16": "atl16Confirmed",
  }


  getAvailabilityWindows(startDate: Date, endDate: Date) {

    const params = {
      startDate: DateTime.fromJSDate(startDate).setZone("utc", { keepLocalTime: true }).toISO() ?? "",
      endDate: DateTime.fromJSDate(endDate).setZone("utc", { keepLocalTime: true }).toISO() ?? ""
    }

    return this.http.get<StaffTripAvailability[]>(`${this.config.dataApiUrl}/trip-staffing/availability-windows`, { params: params }).pipe(
      switchMap(windows => {
        return DataTransform.replaceDatesArray(windows, this.staffTripAvailabilityDateFields)
      })
    )
  }

  getTripStaffingInfo(startDate: Date, endDate: Date) {
    const params = {
      startDate: DateTime.fromJSDate(startDate).setZone("utc", { keepLocalTime: true }).toISO() ?? "",
      endDate: DateTime.fromJSDate(endDate).setZone("utc", { keepLocalTime: true }).toISO() ?? ""
    }

    return this.http.get<TripStaffingView[]>(`${this.config.dataApiUrl}/trip-staffing/trips`, { params: params }).pipe(
      switchMap(tripStaffInfo => {
        return DataTransform.replaceDatesArray(tripStaffInfo, this.tripStaffingViewDateFields)
      })
    )
  }

  getTripStaffingInfoForTrip(tripId: number) {
    return this.http.get<TripStaffingView>(`${this.config.dataApiUrl}/trip-staffing/trip/${tripId.toString()}`).pipe(
      switchMap(info => {
        return DataTransform.replaceDates(info, this.tripStaffingViewDateFields)
      })
    )
  }

  getTripWeeks(startDate: Date, endDate: Date) {
    const params = {
      startDate: DateTime.fromJSDate(startDate).setZone("utc", { keepLocalTime: true }).toISO() ?? "",
      endDate: DateTime.fromJSDate(endDate).setZone("utc", { keepLocalTime: true }).toISO() ?? ""
    }

    return this.http.get<StandardTripWeek[]>(`${this.config.dataApiUrl}/trip-staffing/trip-weeks`, { params: params }).pipe(
      switchMap(weeks => {
        return DataTransform.replaceDatesArray(weeks, this.tripWeeksDateFields)
      })
    )
  }

  getTripStaffingDetail(tripId: number): Observable<TripStaffingDetail> {
    return this.http.get<TripStaffingDetail>(`${this.config.dataApiUrl}/trip-staffing/trip/${tripId}/detail`)
  }

  updateTripStaffingDetail(tripId: number, data: Partial<TripStaffingDetail>) {
    const { id, ...putData } = data

    return this.http.put<TripStaffingDetail>(`${this.config.dataApiUrl}/trip-staffing/trip/${tripId}/detail`, putData)
  }

  isStaffAssignedToTrip(staffId: number, tripStaffingInfo: TripStaffingView) {
    return tripStaffingInfo.tlId === staffId ||
      tripStaffingInfo.atl1 === staffId ||
      tripStaffingInfo.atl2 === staffId ||
      tripStaffingInfo.atl3 === staffId ||
      tripStaffingInfo.atl4 === staffId ||
      tripStaffingInfo.atl5 === staffId ||
      tripStaffingInfo.atl6 === staffId ||
      tripStaffingInfo.atl7 === staffId ||
      tripStaffingInfo.atl8 === staffId ||
      tripStaffingInfo.atl9 === staffId ||
      tripStaffingInfo.atl10 === staffId ||
      tripStaffingInfo.atl11 === staffId ||
      tripStaffingInfo.atl12 === staffId ||
      tripStaffingInfo.atl13 === staffId ||
      tripStaffingInfo.atl14 === staffId ||
      tripStaffingInfo.atl15 === staffId ||
      tripStaffingInfo.atl16 === staffId ||
      tripStaffingInfo.siteLeaderId === staffId

  }

  isStaffConfirmedForTrip(staffId: number, tripStaffingInfo: TripStaffingFields) {
    return (tripStaffingInfo.tlId === staffId && tripStaffingInfo.tlConfirmed === 1) ||
      (tripStaffingInfo.atl1 === staffId && tripStaffingInfo.atl1Confirmed === 1) ||
      (tripStaffingInfo.atl2 === staffId && tripStaffingInfo.atl2Confirmed === 1) ||
      (tripStaffingInfo.atl3 === staffId && tripStaffingInfo.atl3Confirmed === 1) ||
      (tripStaffingInfo.atl4 === staffId && tripStaffingInfo.atl4Confirmed === 1) ||
      (tripStaffingInfo.atl5 === staffId && tripStaffingInfo.atl5Confirmed === 1) ||
      (tripStaffingInfo.atl6 === staffId && tripStaffingInfo.atl6Confirmed === 1) ||
      (tripStaffingInfo.atl7 === staffId && tripStaffingInfo.atl7Confirmed === 1) ||
      (tripStaffingInfo.atl8 === staffId && tripStaffingInfo.atl8Confirmed === 1) ||
      (tripStaffingInfo.atl9 === staffId && tripStaffingInfo.atl9Confirmed === 1) ||
      (tripStaffingInfo.atl10 === staffId && tripStaffingInfo.atl10Confirmed === 1) ||
      (tripStaffingInfo.atl11 === staffId && tripStaffingInfo.atl11Confirmed === 1) ||
      (tripStaffingInfo.atl12 === staffId && tripStaffingInfo.atl12Confirmed === 1) ||
      (tripStaffingInfo.atl13 === staffId && tripStaffingInfo.atl13Confirmed === 1) ||
      (tripStaffingInfo.atl14 === staffId && tripStaffingInfo.atl14Confirmed === 1) ||
      (tripStaffingInfo.atl15 === staffId && tripStaffingInfo.atl15Confirmed === 1) ||
      (tripStaffingInfo.atl16 === staffId && tripStaffingInfo.atl16Confirmed === 1) ||
      (tripStaffingInfo.siteLeaderId === staffId && tripStaffingInfo.siteLeaderConfirmed === 1)


  }

  getNextOpenAtl(tripStaffingInfo: TripStaffingView): keyof AtlKeys | undefined {
    if (!tripStaffingInfo.atl1)
      return "atl1"
    if (!tripStaffingInfo.atl2)
      return "atl2"
    if (!tripStaffingInfo.atl3)
      return "atl3"
    if (!tripStaffingInfo.atl4)
      return "atl4"
    if (!tripStaffingInfo.atl5)
      return "atl5"
    if (!tripStaffingInfo.atl6)
      return "atl6"
    if (!tripStaffingInfo.atl7)
      return "atl7"
    if (!tripStaffingInfo.atl8)
      return "atl8"
    if (!tripStaffingInfo.atl9)
      return "atl9"
    if (!tripStaffingInfo.atl10)
      return "atl10"
    if (!tripStaffingInfo.atl11)
      return "atl11"
    if (!tripStaffingInfo.atl12)
      return "atl12"
    if (!tripStaffingInfo.atl13)
      return "atl13"
    if (!tripStaffingInfo.atl14)
      return "atl14"
    if (!tripStaffingInfo.atl15)
      return "atl15"
    if (!tripStaffingInfo.atl16)
      return "atl16"


    return undefined
  }

  getAvailableAtlSlotCount(tripStaffingInfo: TripStaffingView) {
    let count = 0

    if (tripStaffingInfo.atl1) count++
    if (tripStaffingInfo.atl2) count++
    if (tripStaffingInfo.atl3) count++
    if (tripStaffingInfo.atl4) count++
    if (tripStaffingInfo.atl5) count++
    if (tripStaffingInfo.atl6) count++
    if (tripStaffingInfo.atl7) count++
    if (tripStaffingInfo.atl8) count++
    if (tripStaffingInfo.atl9) count++
    if (tripStaffingInfo.atl10) count++
    if (tripStaffingInfo.atl11) count++
    if (tripStaffingInfo.atl12) count++
    if (tripStaffingInfo.atl13) count++
    if (tripStaffingInfo.atl14) count++
    if (tripStaffingInfo.atl15) count++
    if (tripStaffingInfo.atl16) count++

    return 16 - count

  }

  getTripStaffRatio(trip: Pick<Trip, "attendees">, staffingDetail: TripStaffingView | undefined) {


    const count = this.getAssignedStaffCount(trip, staffingDetail)

    return Math.round(trip.attendees / count)

  }

  /**
   * Returns the number of unique staff assigned to the trip.  NOTE: Excludes site leader per Taylor
   * @param trip 
   * @param staffingDetail 
   * @returns 
   */
  getAssignedStaffCount(trip: Pick<Trip, "attendees">, staffingDetail: TripStaffingView | undefined) {
    let count = 0

    

    if (!staffingDetail)
      return 9999

    count += staffingDetail.tlId && staffingDetail.tlConfirmed ? 1 : 0
    count += staffingDetail.atl1 && staffingDetail.atl1Confirmed ? 1 : 0
    count += staffingDetail.atl2 && staffingDetail.atl2Confirmed ? 1 : 0
    count += staffingDetail.atl3 && staffingDetail.atl3Confirmed ? 1 : 0
    count += staffingDetail.atl4 && staffingDetail.atl4Confirmed ? 1 : 0
    count += staffingDetail.atl5 && staffingDetail.atl5Confirmed ? 1 : 0
    count += staffingDetail.atl6 && staffingDetail.atl6Confirmed ? 1 : 0
    count += staffingDetail.atl7 && staffingDetail.atl7Confirmed ? 1 : 0
    count += staffingDetail.atl8 && staffingDetail.atl8Confirmed ? 1 : 0
    count += staffingDetail.atl9 && staffingDetail.atl9Confirmed ? 1 : 0
    count += staffingDetail.atl10 && staffingDetail.atl10Confirmed ? 1 : 0
    count += staffingDetail.atl11 && staffingDetail.atl11Confirmed ? 1 : 0
    count += staffingDetail.atl12 && staffingDetail.atl12Confirmed ? 1 : 0
    count += staffingDetail.atl13 && staffingDetail.atl13Confirmed ? 1 : 0
    count += staffingDetail.atl14 && staffingDetail.atl14Confirmed ? 1 : 0
    count += staffingDetail.atl15 && staffingDetail.atl15Confirmed ? 1 : 0
    count += staffingDetail.atl16 && staffingDetail.atl16Confirmed ? 1 : 0


    if (count === 0)
      return 9999
    else
      return count

  }

  getNumberOfStaffRequired(trip: Pick<Trip, "attendees">, staffingDetail: TripStaffingView | undefined, targetRatio: number) {

    let leadersNeeded = Math.round(trip.attendees / targetRatio)

    if (leadersNeeded < this.minimumStaffPerTrip) leadersNeeded = this.minimumStaffPerTrip

    const currentStaff = this.getAssignedStaffCount(trip, staffingDetail)

    return leadersNeeded - currentStaff


  }


  setStaffAvailable(staffId: number, startDate: Date, endDate: Date, reloadYear?: number) {
    const body: PutStaffAvailabilityBody = {
      startDate: DateTime.fromJSDate(startDate).toISODate() ?? "",
      endDate: DateTime.fromJSDate(endDate).toISODate() ?? "",
      type: StaffAvailabilityType.available,
      reloadYear: reloadYear
    }
    return this.http.put<StaffTripAvailability[]>(`${this.config.dataApiUrl}/trip-staffing/${staffId.toString()}/availability`, body).pipe(
      switchMap(windows => {
        return DataTransform.replaceDatesArray(windows, this.staffTripAvailabilityDateFields)
      })
    )

  }

  setStaffUnavailable(staffId: number, startDate: Date, endDate: Date, reloadYear?: number) {
    const body: PutStaffAvailabilityBody = {
      startDate: DateTime.fromJSDate(startDate).toISODate() ?? "",
      endDate: DateTime.fromJSDate(endDate).toISODate() ?? "",
      type: StaffAvailabilityType.unavailable,
      reloadYear: reloadYear
    }
    return this.http.put<StaffTripAvailability[]>(`${this.config.dataApiUrl}/trip-staffing/${staffId.toString()}/availability`, body).pipe(
      switchMap(windows => {
        return DataTransform.replaceDatesArray(windows, this.staffTripAvailabilityDateFields)
      })
    )
  }

  clearStaffAvailability(staffId: number, startDate: Date, endDate: Date, reloadYear?: number) {
    const body: PutStaffAvailabilityBody = {
      startDate: DateTime.fromJSDate(startDate).toISODate() ?? "",
      endDate: DateTime.fromJSDate(endDate).toISODate() ?? "",
      type: "clear",
      reloadYear: reloadYear
    }
    return this.http.put<StaffTripAvailability[]>(`${this.config.dataApiUrl}/trip-staffing/${staffId.toString()}/availability`, body).pipe(
      switchMap(windows => {
        return DataTransform.replaceDatesArray(windows, this.staffTripAvailabilityDateFields)
      })
    )
  }

  getStaffIdsForTrip(tripStaffingInfo: StaffIdFields & StaffConfirmedFields, confirmedType: "confirmed" | "unconfirmed" | "any" = "confirmed") {

    const staffIds: number[] = []


    for (const key in this.staffConfirmedMap) {
      if (Object.prototype.hasOwnProperty.call(this.staffConfirmedMap, key)) {
        const staffIdKey = key as keyof StaffIdFields
        const confirmKey = this.staffConfirmedMap[staffIdKey];

        if (tripStaffingInfo[staffIdKey] && (
          confirmedType === "any" ||
          confirmedType === "unconfirmed" && !tripStaffingInfo[confirmKey] ||
          confirmedType === "confirmed" && tripStaffingInfo[confirmKey]
        )
        ) {
          staffIds.push(tripStaffingInfo[staffIdKey])
        }

      }
    }

    return staffIds
  }

  getLimitedStaffForTrips(tripIds: number[]): Observable<GetLimitedStaffResponse> {
    const params = new HttpParams()
    .set("tripIds", tripIds.join(","))
    return this.http.get<GetLimitedStaffResponse>(`${this.config.dataApiUrl}/trip-staffing/staff-limited`, {params: params})
  }

  confirmStaffForTrip(staffId: number, tripStaffingInfo: TripStaffingView) {
    let field: keyof TripsSub | undefined
    let isTl: boolean = false
    let isSl: boolean = false

    if (tripStaffingInfo.tlId === staffId) isTl = true
    if (tripStaffingInfo.atl1 === staffId) field = "atl1Confirmed"
    if (tripStaffingInfo.atl2 === staffId) field = "atl2Confirmed"
    if (tripStaffingInfo.atl3 === staffId) field = "atl3Confirmed"
    if (tripStaffingInfo.atl4 === staffId) field = "atl4Confirmed"
    if (tripStaffingInfo.atl5 === staffId) field = "atl5Confirmed"
    if (tripStaffingInfo.atl6 === staffId) field = "atl6Confirmed"
    if (tripStaffingInfo.atl7 === staffId) field = "atl7Confirmed"
    if (tripStaffingInfo.atl8 === staffId) field = "atl8Confirmed"
    if (tripStaffingInfo.atl9 === staffId) field = "atl9Confirmed"
    if (tripStaffingInfo.atl10 === staffId) field = "atl10Confirmed"
    if (tripStaffingInfo.atl11 === staffId) field = "atl11Confirmed"
    if (tripStaffingInfo.atl12 === staffId) field = "atl12Confirmed"
    if (tripStaffingInfo.atl13 === staffId) field = "atl13Confirmed"
    if (tripStaffingInfo.atl14 === staffId) field = "atl14Confirmed"
    if (tripStaffingInfo.atl15 === staffId) field = "atl15Confirmed"
    if (tripStaffingInfo.atl16 === staffId) field = "atl16Confirmed"
    if (tripStaffingInfo.siteLeaderId === staffId) isSl = true

    if (isTl) {
      return this.tripService.updateTrip(tripStaffingInfo.id, { tlPub: 1 }).pipe(
        switchMap(value => of(void 0))
      )
    }
    else if (isSl) {
      return this.tripService.updateTrip(tripStaffingInfo.id, {siteLeaderConfirmed: 1}).pipe(
        switchMap(value => of(void 0))
      )
    
    }
    else if (field) {
      return this.subtripService.updateSubtrip(tripStaffingInfo.subtripId, { [field]: 1 }).pipe(
        switchMap(value => of(void 0))
      )
    }
    else {
      return of(void 0)
    }

  }

  unconfirmStaffForTrip(staffId: number, tripStaffingInfo: TripStaffingView) {
    let field: keyof TripsSub | undefined
    let isTl: boolean = false
    let isSl: boolean = false

    if (tripStaffingInfo.tlId === staffId) isTl = true
    if (tripStaffingInfo.atl1 === staffId) field = "atl1Confirmed"
    if (tripStaffingInfo.atl2 === staffId) field = "atl2Confirmed"
    if (tripStaffingInfo.atl3 === staffId) field = "atl3Confirmed"
    if (tripStaffingInfo.atl4 === staffId) field = "atl4Confirmed"
    if (tripStaffingInfo.atl5 === staffId) field = "atl5Confirmed"
    if (tripStaffingInfo.atl6 === staffId) field = "atl6Confirmed"
    if (tripStaffingInfo.atl7 === staffId) field = "atl7Confirmed"
    if (tripStaffingInfo.atl8 === staffId) field = "atl8Confirmed"
    if (tripStaffingInfo.atl9 === staffId) field = "atl9Confirmed"
    if (tripStaffingInfo.atl10 === staffId) field = "atl10Confirmed"
    if (tripStaffingInfo.atl11 === staffId) field = "atl11Confirmed"
    if (tripStaffingInfo.atl12 === staffId) field = "atl12Confirmed"
    if (tripStaffingInfo.atl13 === staffId) field = "atl13Confirmed"
    if (tripStaffingInfo.atl14 === staffId) field = "atl14Confirmed"
    if (tripStaffingInfo.atl15 === staffId) field = "atl15Confirmed"
    if (tripStaffingInfo.atl16 === staffId) field = "atl16Confirmed"
    if (tripStaffingInfo.siteLeaderId === staffId) isSl = true

    if (isTl) {
      return this.tripService.updateTrip(tripStaffingInfo.id, { tlPub: 0 }).pipe(
        switchMap(value => of(void 0))
      )
    }
    else if (isSl) {
      return this.tripService.updateTrip(tripStaffingInfo.id, {siteLeaderConfirmed: 0}).pipe(
        switchMap(value => of(void 0)))
    }
    else if (field) {
      return this.subtripService.updateSubtrip(tripStaffingInfo.subtripId, { [field]: 0 }).pipe(
        switchMap(value => of(void 0))
      )
    }
    else {
      return of(void 0)
    }

  }



  unassignStaffFromTrip(staffId: number, tripStaffingInfo: TripStaffingView) {
    let field: keyof TripsSub | undefined
    let isTl: boolean = false
    let isSl: boolean = false

    if (tripStaffingInfo.tlId === staffId) isTl = true
    if (tripStaffingInfo.atl1 === staffId) field = "atl1"
    if (tripStaffingInfo.atl2 === staffId) field = "atl2"
    if (tripStaffingInfo.atl3 === staffId) field = "atl3"
    if (tripStaffingInfo.atl4 === staffId) field = "atl4"
    if (tripStaffingInfo.atl5 === staffId) field = "atl5"
    if (tripStaffingInfo.atl6 === staffId) field = "atl6"
    if (tripStaffingInfo.atl7 === staffId) field = "atl7"
    if (tripStaffingInfo.atl8 === staffId) field = "atl8"
    if (tripStaffingInfo.atl9 === staffId) field = "atl9"
    if (tripStaffingInfo.atl10 === staffId) field = "atl10"
    if (tripStaffingInfo.atl11 === staffId) field = "atl11"
    if (tripStaffingInfo.atl12 === staffId) field = "atl12"
    if (tripStaffingInfo.atl13 === staffId) field = "atl13"
    if (tripStaffingInfo.atl14 === staffId) field = "atl14"
    if (tripStaffingInfo.atl15 === staffId) field = "atl15"
    if (tripStaffingInfo.atl16 === staffId) field = "atl16"
    if (tripStaffingInfo.siteLeaderId === staffId) isSl = true

    if (isTl) {
      return this.tripService.updateTrip(tripStaffingInfo.id, { staffId: 0 }).pipe(
        switchMap(value => of(void 0))
      )
    }
    else if (isSl) {
      return this.tripService.updateTrip(tripStaffingInfo.id, { siteLeaderId: 0}).pipe(
        switchMap(value => of(void 0))
      )
    }
    else if (field) {
      return this.subtripService.updateSubtrip(tripStaffingInfo.subtripId, { [field]: 0 }).pipe(
        switchMap(value => of(void 0))
      )
    }
    else {
      return of(void 0)
    }

  }

  getTripHistoryForStaff(staffId: number): Observable<StaffTripHistoryGetResult> {
    return this.http.get<StaffTripHistoryGetResult>(`${this.config.dataApiUrl}/trip-staffing/${staffId.toString()}/history`).pipe(
      switchMap(result => {
        return forkJoin([
          DataTransform.replaceDatesArray(result.tlTrips, this.staffHistoryTripDateFields),
          DataTransform.replaceDatesArray(result.atlTrips, this.staffHistoryTripDateFields)
        ]).pipe(
          switchMap(([tlTrips, atlTrips]) => {
            return of({
              tlTrips: tlTrips,
              atlTrips: atlTrips
            })
          })
        )
      }
      )
    )
  }

}
